import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { Monitor, Loader2, AlertTriangle, Copy, Check, RefreshCw, Wifi, WifiOff, VideoOff, Shield } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Badge } from '../../components/ui/badge';
import { toast, Toaster } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const WS_URL = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');

// STUN servers
const ICE_SERVERS = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
  { urls: 'stun:stun2.l.google.com:19302' },
];

/**
 * Full-page Screen Share Viewer (Pop-out mode)
 * Accessible via /admin/support/session/:sessionId
 */
export const AdminSupportSessionPage = () => {
  const { sessionId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const adminViewToken = searchParams.get('token');
  
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [error, setError] = useState(null);
  const [tenantConnected, setTenantConnected] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [sessionInfo, setSessionInfo] = useState(null);
  
  // Refs
  const videoRef = useRef(null);
  const wsRef = useRef(null);
  const pcRef = useRef(null);
  
  // Build invite URL
  const inviteUrl = sessionInfo?.tenant_join_url 
    ? `${BACKEND_URL}${sessionInfo.tenant_join_url}`
    : '';
  
  // Verify admin auth on mount
  useEffect(() => {
    const adminToken = localStorage.getItem('admin_token');
    if (!adminToken) {
      toast.error('Admin authentication required');
      navigate('/admin/login');
      return;
    }
    
    if (!sessionId || !adminViewToken) {
      toast.error('Invalid session parameters');
      navigate('/admin/tenants');
      return;
    }
    
    setIsAuthenticated(true);
    
    // Fetch session info
    fetchSessionInfo(adminToken);
  }, [sessionId, adminViewToken, navigate]);
  
  // Fetch session info from API
  const fetchSessionInfo = async (token) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/support-sessions/${sessionId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSessionInfo(data);
      } else {
        setError('Failed to load session info');
      }
    } catch (err) {
      console.error('Failed to fetch session info:', err);
    }
  };
  
  // Copy invite link
  const copyInviteLink = useCallback(() => {
    navigator.clipboard.writeText(inviteUrl);
    setCopied(true);
    toast.success('Invite link copied');
    setTimeout(() => setCopied(false), 2000);
  }, [inviteUrl]);
  
  // Clean up
  const cleanup = useCallback(() => {
    if (pcRef.current) {
      pcRef.current.close();
      pcRef.current = null;
    }
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setConnectionStatus('disconnected');
    setTenantConnected(false);
  }, []);
  
  // Initialize WebSocket when authenticated
  useEffect(() => {
    if (!isAuthenticated || !sessionId || !adminViewToken) {
      return;
    }
    
    setConnectionStatus('connecting');
    setError(null);
    
    const wsUrl = `${WS_URL}/api/ws/support/${sessionId}?token=${adminViewToken}&role=admin`;
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;
    
    ws.onopen = () => {
      console.log('Pop-out WebSocket connected');
      setConnectionStatus('waiting');
    };
    
    ws.onmessage = async (event) => {
      const data = JSON.parse(event.data);
      
      switch (data.type) {
        case 'config':
          initializePeerConnection(data.ice_servers || ICE_SERVERS);
          break;
          
        case 'peer_joined':
          if (data.role === 'tenant') {
            setTenantConnected(true);
            toast.success('Tenant connected');
          }
          break;
          
        case 'peer_left':
          if (data.role === 'tenant') {
            setTenantConnected(false);
            setConnectionStatus('waiting');
            if (videoRef.current) videoRef.current.srcObject = null;
            toast.info('Tenant disconnected');
          }
          break;
          
        case 'offer':
          if (pcRef.current && data.sdp) {
            try {
              await pcRef.current.setRemoteDescription(new RTCSessionDescription({
                type: 'offer',
                sdp: data.sdp
              }));
              const answer = await pcRef.current.createAnswer();
              await pcRef.current.setLocalDescription(answer);
              ws.send(JSON.stringify({ type: 'answer', sdp: answer.sdp }));
            } catch (err) {
              console.error('Offer handling failed:', err);
              setError('Failed to establish connection');
            }
          }
          break;
          
        case 'ice_candidate':
          if (pcRef.current && data.candidate) {
            try {
              await pcRef.current.addIceCandidate(new RTCIceCandidate(data.candidate));
            } catch (err) {
              console.error('ICE candidate failed:', err);
            }
          }
          break;
          
        case 'status_update':
          if (data.status === 'sharing') setConnectionStatus('connected');
          break;
          
        case 'sharing_stopped':
          setConnectionStatus('waiting');
          setTenantConnected(false);
          if (videoRef.current) videoRef.current.srcObject = null;
          toast.info('Tenant stopped sharing');
          break;
          
        case 'connection_failed':
          setConnectionStatus('failed');
          setError(data.reason || 'Connection failed');
          break;
          
        case 'error':
          setError(data.message);
          break;
          
        default:
          break;
      }
    };
    
    ws.onerror = () => {
      setError('Connection error');
      setConnectionStatus('failed');
    };
    
    ws.onclose = () => {
      console.log('Pop-out WebSocket closed');
    };
    
    return () => cleanup();
  }, [isAuthenticated, sessionId, adminViewToken, cleanup]);
  
  // Initialize WebRTC
  const initializePeerConnection = useCallback((iceServers) => {
    const pc = new RTCPeerConnection({ iceServers });
    pcRef.current = pc;
    
    pc.ontrack = (event) => {
      if (videoRef.current && event.streams[0]) {
        videoRef.current.srcObject = event.streams[0];
        setConnectionStatus('connected');
      }
    };
    
    pc.onicecandidate = (event) => {
      if (event.candidate && wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: 'ice_candidate',
          candidate: event.candidate.toJSON()
        }));
      }
    };
    
    pc.onconnectionstatechange = () => {
      switch (pc.connectionState) {
        case 'connected':
          setConnectionStatus('connected');
          break;
        case 'disconnected':
        case 'closed':
          setConnectionStatus('waiting');
          break;
        case 'failed':
          setConnectionStatus('failed');
          setError('Connection failed - possible firewall/NAT issue');
          break;
        default:
          break;
      }
    };
    
    pc.ondatachannel = (event) => {
      event.channel.onmessage = (e) => console.log('Data channel:', e.data);
    };
  }, []);
  
  // Get status display
  const getStatusDisplay = () => {
    switch (connectionStatus) {
      case 'connected':
        return { icon: Wifi, color: 'text-green-500', bg: 'bg-green-500/10', text: 'Connected - Viewing Screen' };
      case 'connecting':
        return { icon: Loader2, color: 'text-yellow-500', bg: 'bg-yellow-500/10', text: 'Connecting...' };
      case 'waiting':
        return { icon: Monitor, color: 'text-blue-500', bg: 'bg-blue-500/10', text: 'Waiting for tenant...' };
      case 'failed':
        return { icon: WifiOff, color: 'text-red-500', bg: 'bg-red-500/10', text: 'Connection Failed' };
      default:
        return { icon: Monitor, color: 'text-gray-500', bg: 'bg-gray-500/10', text: 'Disconnected' };
    }
  };
  
  const status = getStatusDisplay();
  const StatusIcon = status.icon;
  
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#0a0f1a] flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-blue-500 animate-spin" />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-[#0a0f1a] flex flex-col">
      <Toaster position="top-center" richColors />
      
      {/* Header */}
      <header className="flex items-center justify-between px-4 py-3 bg-[#0f1624] border-b border-gray-800 shrink-0">
        <div className="flex items-center gap-3">
          <Shield className="h-5 w-5 text-blue-500" />
          <div>
            <h1 className="text-white font-semibold">Support Session Viewer</h1>
            <p className="text-xs text-gray-400">
              {sessionInfo?.tenant_name || 'Loading...'}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <Badge variant="outline" className="text-xs bg-gray-800/50 text-gray-300 border-gray-600">
            <VideoOff className="h-3 w-3 mr-1" />
            Not recorded
          </Badge>
          
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${status.bg}`}>
            <StatusIcon className={`h-4 w-4 ${status.color} ${connectionStatus === 'connecting' ? 'animate-spin' : ''}`} />
            <span className={`text-sm ${status.color}`}>{status.text}</span>
          </div>
          
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              cleanup();
              window.close();
            }}
            className="bg-gray-800 border-gray-700 text-gray-300 hover:bg-gray-700"
          >
            End Session
          </Button>
        </div>
      </header>
      
      {/* Main content */}
      <main className="flex-1 flex flex-col min-h-0 overflow-hidden">
        {/* Invite link bar (when waiting) */}
        {connectionStatus === 'waiting' && !tenantConnected && inviteUrl && (
          <div className="px-4 py-3 bg-[#0f1624] border-b border-gray-800 shrink-0">
            <div className="flex items-center gap-3 max-w-4xl mx-auto">
              <span className="text-sm text-gray-400 shrink-0">Invite link:</span>
              <code className="flex-1 px-3 py-1.5 bg-gray-800 border border-gray-700 rounded text-xs text-gray-300 truncate">
                {inviteUrl}
              </code>
              <Button 
                onClick={copyInviteLink} 
                variant="outline" 
                size="sm"
                className="shrink-0 bg-gray-800 border-gray-700 text-gray-300 hover:bg-gray-700"
              >
                {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
              </Button>
            </div>
          </div>
        )}
        
        {/* Error */}
        {error && (
          <div className="px-4 py-2 shrink-0">
            <Alert className="max-w-2xl mx-auto border-red-800 bg-red-900/20">
              <AlertTriangle className="h-4 w-4 text-red-400" />
              <AlertDescription className="text-red-300 text-sm">
                {error}
              </AlertDescription>
            </Alert>
          </div>
        )}
        
        {/* Video */}
        <div className="flex-1 relative min-h-0">
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className="absolute inset-0 w-full h-full object-contain"
            style={{ background: '#0a0f1a' }}
          />
          
          {connectionStatus !== 'connected' && (
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <StatusIcon className={`h-16 w-16 mb-4 ${status.color} opacity-50 ${connectionStatus === 'connecting' ? 'animate-spin' : ''}`} />
              <p className="text-xl text-white/80">{status.text}</p>
              {connectionStatus === 'waiting' && (
                <p className="text-sm text-gray-500 mt-2">Share the invite link with the tenant</p>
              )}
              {connectionStatus === 'failed' && (
                <Button 
                  onClick={() => window.location.reload()} 
                  variant="outline" 
                  className="mt-4 bg-gray-800 border-gray-700 text-gray-300"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Retry
                </Button>
              )}
            </div>
          )}
        </div>
      </main>
      
      {/* Footer */}
      <footer className="px-4 py-2 bg-[#0f1624] border-t border-gray-800 shrink-0">
        <div className="flex items-center justify-between text-xs text-gray-500">
          <span>Session: {sessionId?.slice(0, 8)}...</span>
          <span>Tenant: {tenantConnected ? 'Connected' : 'Not connected'}</span>
          <span>
            Expires: {sessionInfo?.expires_at 
              ? new Date(sessionInfo.expires_at).toLocaleTimeString() 
              : 'Unknown'}
          </span>
        </div>
      </footer>
    </div>
  );
};

export default AdminSupportSessionPage;
